
import { Component, Input, ViewChild, Output, EventEmitter, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { environment } from '../../../environments/environment';
import { publicHelpers } from 'globals/helpers/public-helpers';
import { colorsByPublicEditProfile } from 'src/app/store/performers.era';
import { itemStatus, ItemStatus, ShareableItemType, shareUrlsByType, StatusType } from 'globals/store/share.data';
import { openDialogGetResult } from './../../helpers/angular-helpers';
import { MatDialog } from '@angular/material/dialog';
import { CodeDialogComponent, CodeDialogData } from './../../components/wizards/code.dialog.component';
import { UiService } from '../../services/ui.service';
import { CommonModule } from '@angular/common';
import { TrafficLightDropdownComponent } from 'src/app/components/misc/traffic-light-dropdown.component';
import { IconComponent } from 'src/app/components/icon/icon.component';
import { CopyUrlComponent } from './copy-url.component';

export type ShareableFormat = 'oneLine' | 'linksBelow';

@Component({
    selector: 'share-code',
    template: `

<ng-template #links>
  <div class="secondary_link" (click)="publicHelpers.openInNewTab(fullUrl !== '' ? fullUrl : (baseUrl + url + shareCode))">{{ openText }} <icon icon="OPEN_IN_NEW12"></icon>
  </div>
  <copy-url class="secondary_link" [fullUrl]="fullUrl" [shareType]="shareType" [shareCode]="shareCode"></copy-url>
  <div class="secondary_link" *ngIf="showEmbed" (click)="openEmbed(fullUrl !== '' ? fullUrl : (baseUrl + url + shareCode))">
    Embed <icon icon="embed12"></icon>
  </div>
  <div class="secondary_link" *ngIf="!hideJson" (click)="openJson(functionsUrl + urlForJson + url + shareCode)">
    JSON Feed <icon icon="embed12"></icon>
  </div>
  <div class="secondary_link" *ngIf="!!emailGenerator" (click)="publicHelpers.openInNewTab(emailGenerator(fullUrl || (baseUrl + url + shareCode)))">
    Email
  </div>
  <div class="print" (click)="doPrint()" *ngIf="showPrint"><icon matIcon="print"></icon></div>
</ng-template>

<ng-template #share>
<!--   <input #shareUrl class="shareable_url" [ngClass]="{grayText: disabled, withDropdown: !!selectedStatus }"[value]="baseUrl + url + shareCode"> -->
  <div class="input-dropdown-wrapper">
    <div class="shareable_url" [ngClass]="{grayText: disabled, withDropdown: !!selectedStatus }">
      {{ fullUrl || (baseUrl + url + shareCode) }}
    </div>
    <traffic-light-dropdown *ngIf="selectedStatus" class="lights-dropdown"
      [selected]="selectedStatus"
      [colorObject]="colorsByStatus"
      [textObject]="namesByStatus"
      [readOnly]="disabled"
      [selections]="statusOptions"
      (select)="changeStatus.emit($event)"
    ></traffic-light-dropdown>
  </div>
  
  <ng-content></ng-content>
  <input #jsonInput class="hidden_url" [value]="functionsUrl + urlForJson + url + shareCode">
</ng-template>

<div class="published">
  <h5 *ngIf="!hideTitle">{{ title | uppercase }}</h5>
  <div class="input-wrapper" *ngIf="shareableFormat === 'oneLine'">
    <ng-container [ngTemplateOutlet]="share"></ng-container>
    <ng-container [ngTemplateOutlet]="links"></ng-container>
  </div>
  <div class="row-wrapper" *ngIf="shareableFormat === 'linksBelow'">
    <div class="links-wrapper"><ng-container [ngTemplateOutlet]="share"></ng-container></div>
    <div class="links-wrapper"><ng-container [ngTemplateOutlet]="links"></ng-container></div>
  </div>
</div>
`,
    styleUrls: ['./share-code.scss'],
    standalone: true,
    imports: [CommonModule, TrafficLightDropdownComponent, IconComponent, CopyUrlComponent]
})

export class ShareCodeComponent implements OnInit {
  baseUrl = environment.joi.shareBase
  functionsUrl = environment['isLocal'] ? environment.functionsURL + 'func/' : environment.joi.shareBase
  url
  @Input() fullUrl = ''
  @Input() openText = 'Preview'
  @Input() shareType: ShareableItemType
  @Input() urlForJson = 'json/';
  @Input() shareCode
  @Input() shareableFormat: ShareableFormat = 'oneLine'
  @Input() disabled = false
  @Input() hideJson = false
  @Input() showEmbed = false
  @Input() hideTitle = false
  @Input() loading = false
  @Input() showPrint = false
  @Input() title = "YOUR SHAREABLE WEBPAGE:"
  @Input() selectedStatus: StatusType | null
  @Input() colorsByStatus: any = colorsByPublicEditProfile // can't type this grr
  @Input() statusOptions: StatusType[] = itemStatus
  @Input() namesByStatus // can be null, then names will just be from the status options.  Sometimes these are different (and they really should always be different in case you want to change the names later without messing up the underlying data; or rather, this should be added if you modify the ids)
  @Input() manualPrint: boolean = false
  @Input() emailGenerator: (code: string) => string

  @Output() print:EventEmitter<void> = new EventEmitter()
  @Output() changeStatus:EventEmitter<any> = new EventEmitter()
  @ViewChild('jsonInput') jsonInput
  publicHelpers = publicHelpers

  constructor(private dialog: MatDialog, private uiService: UiService) {}


  
  doPrint() {
    if (this.manualPrint) this.print.emit()
    else this.uiService.print()
  }


  ngOnInit() {
    this.url = `${shareUrlsByType[this.shareType]}/`
  }

  copyTextToClipboard(text: string) {
    navigator['clipboard'].writeText(text)
  }

  async openEmbed(embedUrl: string) {
    const embedCode = `<div style="position: relative; height: 70vh; max-width: 100%; border: 1px solid #ddd;"><iframe src="${embedUrl}" frameborder="0" style="position: absolute; width: 100%; height: 100%;"></iframe></div>`
    const data: CodeDialogData = {
      title: 'Embed',
      description: 'Copy & paste this snippet into your code to embed this published timeline on your website.',
      code: embedCode,
      copyText: 'COPY CODE',
      successMessage: 'CODE COPIED!',
    }
    const open = await openDialogGetResult(this.dialog, CodeDialogComponent, { width: '480px', disableClose: false, autoFocus: false, data })
  }

  async openJson(jsonUrl: string) {
    const data: CodeDialogData = {
      title: 'JSON feed',
      description: 'Share this URL with your website developer so they can use this timeline as a data source for a website of your own design',
      code: jsonUrl,
      copyText: 'COPY URL',
      successMessage: 'URL COPIED!',
    }
    const open = await openDialogGetResult(this.dialog, CodeDialogComponent, { width: '480px', disableClose: false, autoFocus: false, data })
  }

}


